import * as React from 'react'
import { Box, Button, CardContent, Pagination, Card } from '@mui/material'
import { styled } from '@mui/material/styles'
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'
import { useMutation } from 'react-query'
import {
  TranslateDto,
  translate,
  VariantDetailOmimData
} from '@/@core/services/app.service'

import { ExternalLinks } from '@/@core/services/api'
import {
  filterObjectsByTextWithRemainder,
  formatVariantInheritance,
  unique
} from '@/@core/utils/utils'
import { Eslint, OpenInNew } from 'mdi-material-ui'
import { useTranslation } from 'react-i18next'

interface Props {
  text: string
  paddingLeft?: number
  fontSize?: number
  fontWeight?: number
  isTranslate?: boolean
}

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={null} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1)
  }
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)'
}))

const TranslationComponent = ({
  text,
  paddingLeft,
  fontSize = 16,
  fontWeight = 500,
  isTranslate = true
}: Props) => {
  const [translatedText, setTranslatedText] = React.useState<any>(null)
  const { i18n } = useTranslation()
  const Translate = useMutation({
    mutationFn: async (data: TranslateDto) => await translate(data),
    onSuccess: (data, params, context) => {
      //post返回的结果
    }
  })

  const translateText = async () => {
    try {
      if (i18n.language === 'zh' && isTranslate) {
        const sourceLanguage = 'en',
          targetLanguage = 'zh'
        const params: TranslateDto = {
          sourceLanguage,
          targetLanguage,
          sourceText: text
        }

        const translatedText = await Translate.mutateAsync(params)

        // 假设 API 的响应包含翻译后的文本
        setTranslatedText(translatedText)
      } else setTranslatedText(text)
    } catch (error) {
      console.error('Translation error:', error)

      setTranslatedText('Translation error')
    }
  }

  React.useEffect(() => {
    translateText()
  }, [text])

  return (
    <Typography
      paddingLeft={paddingLeft}
      fontSize={fontSize}
      fontWeight={fontWeight}
      key={text}
    >
      {i18n.language === 'zh'
        ? `${text} / ${translatedText ? translatedText : 'Translating...'}`
        : text}
    </Typography>
  )
}

const ClinicalSynopsisDetail = React.forwardRef(
  (props: { value: any }, ref) => {
    const [inheritances, setInheritances] = React.useState<string[]>([])
    const { t, i18n } = useTranslation()
    const [page, setPage] = React.useState(1)

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
      setPage(value)
    }

    const isTranslate = React.useMemo(() => true, [props.value])

    React.useEffect(() => {
      const arr = unique(props.value, 'phenoName')
        .map((value: any) => {
          return Object.getOwnPropertyNames(value.clinicalSynopsis)
            .map((key) => {
              const item = value.clinicalSynopsis[key]
              if (value.clinicalSynopsis['INHERITANCE']) {
                if (key === 'INHERITANCE') {
                  const inheritance = formatVariantInheritance(
                    Array.from([...new Set(item)])
                      .join(',')
                      .toLowerCase()
                  )

                  return inheritance
                } else return t('null')
              } else return t('null')
            })
            .filter((item) => item)
        })
        .flat()

      //
      console.log(arr)

      setInheritances(arr)
    }, [])

    const render = (value: { [x: string]: any }) => {
      const list = [] as any[]

      Object.getOwnPropertyNames(value).map((key) => {
        const item = value[key]

        list.push(
          <TranslationComponent
            isTranslate={isTranslate}
            text={key}
          ></TranslationComponent>
        )

        if (Array.isArray(item)) {
          item.map((i) => {
            list.push(
              <TranslationComponent
                isTranslate={isTranslate}
                text={`- ${i}`}
                paddingLeft={10}
              ></TranslationComponent>
            )
          })
        } else if (item.constructor === Object) {
          Object.getOwnPropertyNames(item).map((key) => {
            const items = item[key]

            list.push(
              <TranslationComponent
                isTranslate={isTranslate}
                text={`+ ${key}`}
                paddingLeft={20}
              ></TranslationComponent>
            )

            items.map((i: any) => {
              list.push(
                <TranslationComponent
                  isTranslate={isTranslate}
                  text={`- ${i}`}
                  paddingLeft={30}
                ></TranslationComponent>
              )
            })
          })
        } else {
          list.push(
            <TranslationComponent
              isTranslate={isTranslate}
              text={JSON.stringify(item)}
              paddingLeft={10}
            ></TranslationComponent>
          )
        }
      })

      return list
    }

    const paginate = (data: any[], pageSize = 3, pageNumber = 1) => {
      // 计算总页数

      const totalPages = Math.ceil(data.length / pageSize)

      // 检查页码是否有效

      if (pageNumber < 1 || pageNumber > totalPages) {
        return [] // 或者抛出一个错误
      }

      // 计算起始索引和结束索引

      const startIndex = (pageNumber - 1) * pageSize

      const endIndex = startIndex + pageSize

      // 返回当前页的数据

      return data.slice(startIndex, endIndex)
    }

    const list = React.useMemo(() => {
      if (props.value.length) {
        return paginate(props.value, 3, page)
      }

      return []
    }, [props.value, page])

    return (
      <Box>
        <Card
          sx={{
            mb: '5px'
          }}
        >
          <CardContent>
            <Pagination
              size="large"
              sx={{
                display: 'flex',
                justifyContent: 'center'
              }}
              count={Math.ceil(props.value.length / 3)}
              page={page}
              onChange={handleChange}
            />
          </CardContent>
        </Card>

        <Box overflow={'auto'} maxHeight={600}>
          {list.map((items: any, index: number) => {
            return (
              <Accordion key={items.phenoName} expanded={true}>
                <AccordionSummary
                  aria-controls="panel1d-content"
                  id="panel1d-header"
                  sx={{
                    userSelect: 'text'
                  }}
                >
                  <Typography
                    onClick={() => {
                      window.open(`${ExternalLinks.omim}/${items.phenoMim}`)
                    }}
                  >
                    <TranslationComponent
                      isTranslate={isTranslate}
                      text={`${inheritances[index]}--${items.phenoName}(OMIM:${items.phenoMim})`}
                      paddingLeft={0}
                      fontSize={18}
                      fontWeight={600}
                    ></TranslationComponent>
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <Box
                    sx={{
                      padding: 5,
                      position: 'relative',
                      pt: 10
                    }}
                  >
                    <Box pb={2}>
                      <Typography
                        component={'h5'}
                        sx={{
                          fontSize: 16,
                          fontWeight: 600
                        }}
                      >
                        {t('Disease Information')}：
                      </Typography>

                      {i18n.language === 'zh' ? (
                        <Box>
                          {items.omimData &&
                          items.omimData.phenoClinicalFeaturesCN &&
                          items.omimData.phenoClinicalFeaturesCN.length ? (
                            items.omimData.phenoClinicalFeaturesCN.map(
                              (item: any[]) => {
                                return (
                                  <Box>
                                    {item
                                      .filter(
                                        (i) => i && i.text && i.text !== '.'
                                      )
                                      .map((i) => {
                                        return (
                                          <Typography
                                            sx={{
                                              textDecoration:
                                                i.value && i.value !== '.'
                                                  ? 'underline'
                                                  : '',
                                              color:
                                                i.value && i.value !== '.'
                                                  ? 'primary.main'
                                                  : '',
                                              cursor:
                                                i.value && i.value !== '.'
                                                  ? 'pointer'
                                                  : ''
                                            }}
                                            component={'span'}
                                            onClick={() => {
                                              if (i.value && i.value !== '.')
                                                window.open(i.value)
                                            }}
                                          >
                                            {`${i.text}${
                                              i.value && i.value !== '.'
                                                ? `(${i.value})`
                                                : ''
                                            }`}
                                          </Typography>
                                        )
                                      })}
                                  </Box>
                                )
                              }
                            )
                          ) : (
                            <Box>
                              <Typography>{t('No Results')}</Typography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          {items.omimData &&
                          items.omimData.phenoClinicalFeatures &&
                          items.omimData.phenoClinicalFeatures.length ? (
                            items.omimData.phenoClinicalFeatures.map(
                              (item: any[]) => {
                                return (
                                  <Box>
                                    {item
                                      .filter(
                                        (i) => i && i.text && i.text !== '.'
                                      )
                                      .map((i) => {
                                        return (
                                          <Typography
                                            sx={{
                                              textDecoration:
                                                i.value && i.value !== '.'
                                                  ? 'underline'
                                                  : '',
                                              color:
                                                i.value && i.value !== '.'
                                                  ? 'primary.main'
                                                  : '',
                                              cursor:
                                                i.value && i.value !== '.'
                                                  ? 'pointer'
                                                  : ''
                                            }}
                                            component={'span'}
                                            onClick={() => {
                                              if (i.value && i.value !== '.')
                                                window.open(i.value)
                                            }}
                                          >
                                            {`${i.text}${
                                              i.value && i.value !== '.'
                                                ? `(${i.value})`
                                                : ''
                                            }`}
                                          </Typography>
                                        )
                                      })}
                                  </Box>
                                )
                              }
                            )
                          ) : (
                            <Box>
                              <Typography>{t('No Results')}</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    <Box pb={2}>
                      <Typography
                        sx={{
                          fontSize: 16,
                          fontWeight: 600
                        }}
                      >
                        {t('Disease details')}：
                      </Typography>

                      {i18n.language === 'zh' ? (
                        <Box>
                          {items.omimData &&
                          items.omimData.phenoDescriptionCN &&
                          items.omimData.phenoDescriptionCN.length ? (
                            filterObjectsByTextWithRemainder(
                              items.omimData.phenoDescriptionCN ?? [],
                              '遗传异质性'
                            ).map((item: any[]) => {
                              return (
                                <Box>
                                  {item
                                    .filter(
                                      (i) => i && i.text && i.text !== '.'
                                    )
                                    .map((i) => {
                                      return (
                                        <Typography
                                          sx={{
                                            textDecoration:
                                              i.value && i.value !== '.'
                                                ? 'underline'
                                                : '',
                                            color:
                                              i.value && i.value !== '.'
                                                ? 'primary.main'
                                                : '',
                                            cursor:
                                              i.value && i.value !== '.'
                                                ? 'pointer'
                                                : ''
                                          }}
                                          component={'span'}
                                          onClick={() => {
                                            if (i.value && i.value !== '.')
                                              window.open(i.value)
                                          }}
                                        >
                                          {`${i.text}${
                                            i.value && i.value !== '.'
                                              ? `(${i.value})`
                                              : ''
                                          }`}
                                        </Typography>
                                      )
                                    })}
                                </Box>
                              )
                            })
                          ) : (
                            <Box>
                              <Typography>{t('No Results')}</Typography>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          {items.omimData &&
                          items.omimData.phenoDescription &&
                          items.omimData.phenoDescription.length ? (
                            filterObjectsByTextWithRemainder(
                              items.omimData.phenoDescription ?? [],
                              '遗传异质性'
                            ).map((item: any[]) => {
                              return (
                                <Box>
                                  {item
                                    .filter(
                                      (i) => i && i.text && i.text !== '.'
                                    )
                                    .map((i) => {
                                      return (
                                        <Typography
                                          sx={{
                                            textDecoration:
                                              i.value && i.value !== '.'
                                                ? 'underline'
                                                : '',
                                            color:
                                              i.value && i.value !== '.'
                                                ? 'primary.main'
                                                : '',
                                            cursor:
                                              i.value && i.value !== '.'
                                                ? 'pointer'
                                                : ''
                                          }}
                                          component={'span'}
                                          onClick={() => {
                                            if (i.value && i.value !== '.')
                                              window.open(i.value)
                                          }}
                                        >
                                          {`${i.text}${
                                            i.value && i.value !== '.'
                                              ? `(${i.value})`
                                              : ''
                                          }`}
                                        </Typography>
                                      )
                                    })}
                                </Box>
                              )
                            })
                          ) : (
                            <Box>
                              <Typography>{t('No Results')}</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>

                    {render(items.clinicalSynopsis)}

                    <Button
                      endIcon={<OpenInNew />}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        window.open(`${ExternalLinks.omim}/${items.phenoMim}`)
                      }}
                      sx={{
                        position: 'absolute',
                        top: 5,
                        right: '1.25rem'
                      }}
                    >
                      OMIM
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      </Box>
    )
  }
)

export default ClinicalSynopsisDetail
